import { useEffect, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlignLeft,
  Award,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  ClipboardList,
  FileText,
  Loader2,
  PencilLine,
  Scale,
  StickyNote,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "convex/_generated/api";
import { SideSheet } from "@/components/ui";
import { DatePicker } from "@/components/ui";
import { Dropdown } from "@/components/ui";
import { Button, Input, Textarea } from "@/components/ui/primitives";
import { springs } from "@/components/ui/motion";
import { useApiMutation } from "@/packages/features-shared";
import {
  TRIGGER_LABEL_KEYS,
  TRIGGER_OPTIONS,
} from "./NewAuditWizard/wizardData";
import { AUDIT_STATUS_CONFIG } from "./constants";
import type { DepthTier, UIAudit } from "@/packages/types";
import { cn, useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AuditEditSheetProps {
  audit: UIAudit;
  open: boolean;
  onClose: () => void;
}

// ─── Form State ─────────────────────────────────────────────────────────────

interface EditForm {
  displayName: string;
  deadline: number;
  description: string;
  trigger: string;
  notes: string;
  auditorRequestText: string;
  depthTier: DepthTier | null;
}

function buildForm(audit: UIAudit): EditForm {
  return {
    displayName: audit.displayName,
    deadline: audit.deadline,
    description: audit.description ?? "",
    trigger: audit.trigger ?? "",
    notes: audit.notes ?? "",
    auditorRequestText: audit.auditorRequestText ?? "",
    depthTier: null,
  };
}

// ─── Animation Variants ──────────────────────────────────────────────────────

const fieldVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.22, ease: "easeOut" as const },
  }),
};

// ─── Trigger Icons ────────────────────────────────────────────────────────────

const TRIGGER_ICONS: Record<string, LucideIcon> = {
  Regulatory: Scale,
  Certification: Award,
  Internal: Building2,
  "Ad-hoc": Zap,
};

// ─── Depth Tier Constants ─────────────────────────────────────────────────────

const TIER_ORDER: DepthTier[] = ["basic", "standard", "deep-dive"];

// ─── Field Label ─────────────────────────────────────────────────────────────

function FieldLabel({
  children,
  required,
  icon: Icon,
}: {
  children: string;
  required?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted" />}
      <span className="text-[12px] font-medium text-secondary">
        {children}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
    </div>
  );
}

// ─── Section Divider ─────────────────────────────────────────────────────────

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-border" />
      <span className="text-[10px] font-semibold tracking-widest uppercase text-muted">
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─── Animated Error ───────────────────────────────────────────────────────────

function AnimatedError({ message }: { message?: string }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.p
          key="error"
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 4 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.15 }}
          className="overflow-hidden text-[12px] text-destructive"
        >
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function AuditEditSheet({ audit, open, onClose }: AuditEditSheetProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [form, setForm] = useState<EditForm>(() => buildForm(audit));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateAudit = useApiMutation(api.audit_mutations.updateAudit, {
    name: "update-audit",
    successMessage: t("audits:edit.success"),
  });

  const { data: currentTier } = useWorkspaceQuery(
    api.audit_depth_tier_mutations.getCurrentTier,
    {
      auditId: audit.id as Parameters<
        typeof updateAudit.mutateAsync
      >[0]["auditId"],
    },
  );

  const { data: preview } = useWorkspaceQuery(
    api.audit_depth_tier_mutations.previewTierChange,
    form.depthTier && form.depthTier !== currentTier
      ? {
          auditId: audit.id as Parameters<
            typeof updateAudit.mutateAsync
          >[0]["auditId"],
          newTier: form.depthTier,
        }
      : "skip",
  );

  const changeTierMutation = useApiMutation(
    api.audit_depth_tier_mutations.changeDepthTier,
    { name: "change-depth-tier" },
  );

  // Re-sync form when sheet opens
  useEffect(() => {
    if (open) {
      setForm(buildForm(audit));
      setErrors({});
    }
  }, [open, audit]);

  // Initialize form.depthTier from query
  useEffect(() => {
    if (currentTier && !form.depthTier) {
      setForm((prev) => ({ ...prev, depthTier: currentTier }));
    }
  }, [currentTier, form.depthTier]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.displayName.trim())
      errs.displayName = t("audits:edit.validation.nameRequired");
    if (!form.deadline)
      errs.deadline = t("audits:edit.validation.dueDateRequired");
    if (!form.trigger)
      errs.trigger = t("audits:edit.validation.triggerRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (form.depthTier && form.depthTier !== currentTier) {
      await changeTierMutation.mutateAsync({
        auditId: audit.id as Parameters<
          typeof updateAudit.mutateAsync
        >[0]["auditId"],
        newTier: form.depthTier,
      });
    }
    await updateAudit.mutateAsync({
      auditId: audit.id as Parameters<
        typeof updateAudit.mutateAsync
      >[0]["auditId"],
      displayName: form.displayName,
      deadline: form.deadline,
      description: form.description || undefined,
      trigger: form.trigger || undefined,
      notes: form.notes || undefined,
      auditorRequestText: form.auditorRequestText || undefined,
    });
    onClose();
  };

  const deadlineDate = form.deadline ? new Date(form.deadline) : undefined;

  const handleDeadlineChange = (date: Date | undefined) => {
    setForm((prev) => ({ ...prev, deadline: date ? date.getTime() : 0 }));
    if (date) setErrors((prev) => ({ ...prev, deadline: "" }));
  };

  const statusConfig = AUDIT_STATUS_CONFIG[audit.status];

  return (
    <SideSheet open={open} onClose={onClose}>
      <SideSheet.Header
        title={t("audits:edit.title")}
        description={audit.code}
        icon={<PencilLine size={18} />}
        tags={[
          {
            label: statusConfig.label,
            variant: "status",
            bgColor: statusConfig.badgeBg,
            color: statusConfig.badgeText,
          },
        ]}
      />

      <SideSheet.Body>
        <div className="space-y-5 py-2">
          {/* Name — full width */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <FieldLabel required icon={FileText}>
              {t("audits:edit.fields.name")}
            </FieldLabel>
            <Input
              placeholder={t("audits:edit.fields.namePlaceholder")}
              value={form.displayName}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, displayName: e.target.value }));
                if (e.target.value.trim())
                  setErrors((prev) => ({ ...prev, displayName: "" }));
              }}
            />
            <AnimatedError message={errors.displayName} />
          </motion.div>

          {/* Due Date + Trigger — 2-col */}
          <motion.div
            className="grid grid-cols-2 gap-4"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            {/* Due Date */}
            <div>
              <FieldLabel required icon={CalendarDays}>
                {t("audits:edit.fields.dueDate")}
              </FieldLabel>
              <DatePicker
                value={deadlineDate}
                onChange={handleDeadlineChange}
                minDate={new Date()}
              >
                <DatePicker.Trigger variant="input" className="w-full" />
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-[60] rounded-[12px] border border-border bg-card shadow-lg"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="flex">
                      <DatePicker.Presets />
                      <DatePicker.Calendar />
                    </div>
                    <DatePicker.Actions />
                  </Popover.Content>
                </Popover.Portal>
              </DatePicker>
              <AnimatedError message={errors.deadline} />
            </div>

            {/* Trigger */}
            <div>
              <FieldLabel required icon={Zap}>
                {t("audits:edit.fields.trigger")}
              </FieldLabel>
              <Dropdown>
                <Dropdown.Trigger>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-button border border-border bg-card px-3 py-2 text-[14px] text-left cursor-pointer"
                  >
                    <span
                      className={form.trigger ? "text-primary" : "text-muted"}
                    >
                      {form.trigger &&
                      TRIGGER_LABEL_KEYS[
                        form.trigger as (typeof TRIGGER_OPTIONS)[number]
                      ]
                        ? t(
                            `audits:${TRIGGER_LABEL_KEYS[form.trigger as (typeof TRIGGER_OPTIONS)[number]]}`,
                          )
                        : t("audits:edit.fields.triggerPlaceholder")}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted" />
                  </button>
                </Dropdown.Trigger>
                <Dropdown.Content side="bottom" align="start">
                  <Dropdown.Label>
                    {t("audits:edit.fields.trigger")}
                  </Dropdown.Label>
                  {TRIGGER_OPTIONS.map((trigger) => {
                    const Icon = TRIGGER_ICONS[trigger];
                    return (
                      <Dropdown.Item
                        key={trigger}
                        onSelect={() => {
                          setForm((prev) => ({ ...prev, trigger }));
                          setErrors((prev) => ({ ...prev, trigger: "" }));
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className="h-3.5 w-3.5 text-muted" />}
                          {t(`audits:${TRIGGER_LABEL_KEYS[trigger]}`)}
                        </div>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Content>
              </Dropdown>
              <AnimatedError message={errors.trigger} />
            </div>
          </motion.div>

          {/* Depth Tier */}
          <SectionDivider label={t("audits:depthTier.change")} />

          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={1.5}
          >
            <div className="grid grid-cols-3 gap-2">
              {TIER_ORDER.map((tier) => {
                const isSelected = form.depthTier === tier;
                return (
                  <button
                    key={tier}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, depthTier: tier }))
                    }
                    className={cn(
                      "relative flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-center transition-all duration-150",
                      isSelected
                        ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                        : "border-border hover:border-accent/40",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                        isSelected
                          ? "border-accent bg-accent"
                          : "border-border",
                      )}
                    >
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={springs.bouncy}
                        >
                          <Check className="h-2.5 w-2.5 text-white" />
                        </motion.div>
                      )}
                    </div>
                    <span
                      className={cn(
                        "text-[11px] font-semibold",
                        isSelected ? "text-accent" : "text-primary",
                      )}
                    >
                      {t(`audits:depthTier.tiers.${tier}.name`)}
                    </span>
                    <span className="text-[10px] text-muted line-clamp-2">
                      {t(`audits:depthTier.tiers.${tier}.description`)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Inline preview */}
            <AnimatePresence mode="wait">
              {form.depthTier && form.depthTier !== currentTier && preview && (
                <motion.div
                  key={preview.type}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="mt-2 rounded-lg border border-border bg-card-hover px-3 py-2"
                >
                  {preview.type === "upgrade" && (
                    <p className="text-[12px] text-secondary">
                      {t("audits:depthTier.upgradePreview", {
                        count: preview.newlyVisibleCount,
                        categories: preview.categoryCount,
                      })}
                    </p>
                  )}
                  {preview.type === "downgrade" && (
                    <div className="flex flex-col gap-1">
                      <p className="text-[12px] text-secondary">
                        {t("audits:depthTier.downgradePreview", {
                          count: preview.hiddenCount,
                          preserved: preview.preservedCount,
                        })}
                      </p>
                      {preview.preservedCount > 0 && (
                        <p className="text-[11px] text-muted">
                          {t("audits:depthTier.downgradeNote")}
                        </p>
                      )}
                    </div>
                  )}
                  {preview.type === "same" && (
                    <p className="text-[12px] text-muted">
                      {t("audits:depthTier.noChange")}
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Section divider */}
          <SectionDivider label={t("audits:edit.sections.additionalDetails")} />

          {/* Description */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <FieldLabel icon={AlignLeft}>
              {t("audits:edit.fields.description")}
            </FieldLabel>
            <Textarea
              placeholder={t("audits:edit.fields.descriptionPlaceholder")}
              rows={5}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </motion.div>

          {/* Notes */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            <FieldLabel icon={StickyNote}>
              {t("audits:edit.fields.notes")}
            </FieldLabel>
            <Textarea
              placeholder={t("audits:edit.fields.notesPlaceholder")}
              rows={4}
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </motion.div>

          {/* Auditor Request Text */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <FieldLabel icon={ClipboardList}>
              {t("audits:edit.fields.auditorInstructions")}
            </FieldLabel>
            <Textarea
              placeholder={t(
                "audits:edit.fields.auditorInstructionsPlaceholder",
              )}
              rows={3}
              value={form.auditorRequestText}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  auditorRequestText: e.target.value,
                }))
              }
            />
          </motion.div>
        </div>
      </SideSheet.Body>

      <SideSheet.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={updateAudit.isPending || changeTierMutation.isPending}
        >
          {updateAudit.isPending || changeTierMutation.isPending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("audits:edit.actions.saving")}
            </span>
          ) : (
            t("audits:edit.actions.saveChanges")
          )}
        </Button>
      </SideSheet.Footer>
    </SideSheet>
  );
}
